'use client';

import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { ArrowLeft, FilterCheck } from '@zoomit/icons';
import { cn, Flex, Link, Skeleton } from '@zoomit/primitives';
import { Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { OriginURL } from '../../constants/apis';
import { isMobile } from '../../constants/media-query';
import { NOTIFICATION_MENU_KEY } from '../../constants/notification-manager';
import {
  useInfiniteNotifications,
  useNotificationCount,
  useReadAllNotifications,
} from '../../hooks/notification';
import Notifications from './Notifications';

type Props = {
  onClose: () => void;
  className?: string;
  showAllButton?: boolean;
};

const NotificationBody = (props: Props) => {
  const { onClose, showAllButton = true } = props;
  const { user: session } = useAuth();
  const notificationQuery = useInfiniteNotifications({
    key: NOTIFICATION_MENU_KEY,
    options: { isRead: false, pageSize: 25 },
  });
  const { mutate: readAll, isLoading: isReadingAll } = useReadAllNotifications();
  const { data: notificationsCount, isLoading: isCountLoading } = useNotificationCount();
  const { ref, inView } = useInView();
  const { fetchNextPage, hasNextPage } = notificationQuery;

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  return (
    <div className={cn('flex h-full flex-col overflow-auto px-2 py-4', props.className)}>
      <Flex justifyContent="space-between" className="border-b-grey6 border-b px-4 pb-2.5">
        <Flex onClick={() => readAll()} style={{ cursor: 'pointer' }} alignItems="center" gap={0.6}>
          <FilterCheck fill={cssVar(theme.primary1)} />
          <Typography variant="subhead-bold" textColor={cssVar(theme.primary1)}>
            {isReadingAll ? 'درحال بارگیری' : 'همه را خواندم'}
          </Typography>
        </Flex>
        <Typography className="fa" textColor={cssVar(theme.grey1)} variant="subhead">
          {isCountLoading ? (
            <Skeleton width={8} height={1.6} />
          ) : (
            `${notificationsCount?.data || 0} اعلان مشاهده نشده دارید`
          )}
        </Typography>
      </Flex>
      <Notifications
        onClose={() => onClose && onClose()}
        notificationQuery={notificationQuery}
        isCountLoading={isCountLoading}
      />
      {hasNextPage && (
        <div style={{ marginTop: '8px' }} ref={ref}>
          <Typography spacing={{ pt: 'x3' }} variant="subhead-bold" textColor={cssVar(theme.primary1)}>
            درحال بارگذاری
          </Typography>
        </div>
      )}
      {showAllButton && notificationQuery.data?.pages.length && (
        <Link href={`${OriginURL}/profile/${session?.profile.sub}/notifications/`}>
          <Button
            spacing={{ mt: 'x6' }}
            variant={isMobile ? 'outline' : 'text'}
            size={isMobile ? 'full width' : 'fit'}
            leftIcon={<ArrowLeft size="sm" className="!fill-primary1" />}
          >
            مشاهده همه اعلانات
          </Button>
        </Link>
      )}
    </div>
  );
};

export default NotificationBody;
